/**
 * DetailedRefereeSearch module renders filters and a referee list for Global Search.
 * It manages filter chips, performs the detailed client search via Redux,
 * and opens modals for messaging and templated emails to referees.
 *
 * Internal state:
 * - `detailFilter`: Array of applied filters used to build the search payload
 * - `tableData`: Array of referee rows returned from the search API
 * - `modalState`: Controls visibility and type of the currently open modal
 *
 * Side effects:
 * - Subscribes to `driverReducer.detailClientSearchInfo` to populate `tableData`
 *
 * External dependencies:
 * - Child components: `Filters`, `FilterChips`, `RefereeList`, `CandidateSubModals`,
 *   `SendEmailByTemplateToSiteModal`
 *
 * @returns {JSX.Element} The Detailed Referee Search view container
 */

import { Col, Empty, Row, Spin } from "antd";
import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import Loader from "@ui/feedback/loader";
import SendEmailByTemplateToSiteModal from "@ui/messaging/send-email-by-template-to-site-modal";
import Filters from "./filter";
import FilterChips from "../shared/filter-chips";
import RefereeList from "./referee-list";

import { FIELD_TYPES } from "../shared/constants";

import "./detailed-referee-search.scss";
import { detailRefereeSearch } from "@redux/actionCreators";

const { TEXT_FIELD, SELECT_FIELD } = FIELD_TYPES;

const defaultFilters = [];

const DetailedRefereeSearch = () => {
  const dispatch = useDispatch();

  const [detailFilter, setDetailFilter] = useState(defaultFilters);
  const [tableData, setTableData] = useState([]);

  const [modalState, setModalState] = useState({ isVisible: false, modalName: null });

  const driverReducer = useSelector(rdc => rdc?.driverReducer, shallowEqual);

  const {
    detailRefereeSearchInfo: {
      resultData: detailRefereeSearchData,
      isLoading: detailRefereeSearchLoading
    }
  } = driverReducer;

  const openModal = ({ modalName, selectedEmailItems = [] }) => {
    if (modalName === "SendEmailByTemplateToSiteModal") {
      setModalState({ isVisible: true, modalName, selectedEmailItems, refereeData: tableData });
    }
  };

  const handleFilterChange = filter => {
    const filterItems = [];
    Object.keys(filter).forEach(key => {
      if (Object.values(filter[key]).length > 0) {
        if (filter[key]?.fieldType === SELECT_FIELD) {
          const filterData = {
            label: filter[key]?.filterName,
            value: filter[key]?.dropDownValue,
            filterId: filter[key]?.filterId,
            filterType: filter[key]?.filterType
          };
          filterItems.push(filterData);
        } else if (filter[key]?.fieldType === TEXT_FIELD) {
          const filterData = {
            label: filter[key]?.filterName,
            value: filter[key]?.textValue,
            filterId: filter[key]?.filterId,
            filterType: filter[key]?.filterType
          };
          filterItems.push(filterData);
        }
      }
    });
    setDetailFilter(previousFiles => [...previousFiles, ...filterItems]);
  };

  const removeFilter = index => {
    const newArray = detailFilter.filter((data, i) => i !== index);
    setDetailFilter(newArray);
    if (newArray.length === 0) {
      setTableData([]);
    }
  };

  const clearDetailFilter = () => {
    setDetailFilter([]);
    setTableData([]);
  };

  const searchPerson = () => {
    const requestFiltersArray = detailFilter.map(filter => ({
      filterId: filter.filterId,
      value: filter.value,
      filterType: filter.filterType
    }));

    const filters = Object.groupBy(requestFiltersArray, item => item.filterType);

    dispatch(detailRefereeSearch(filters));
  };

  const closeModal = () => {
    setModalState({ isVisible: false, modalName: null });
  };

  // fill table with referee search data
  useEffect(() => {
    if (detailRefereeSearchData && detailRefereeSearchData.Data) {
      setTableData(detailRefereeSearchData.Data || []);
    }
  }, [detailRefereeSearchData]);

  return (
    <div className="global-search detailed-referee-search-container">
      <Spin
        indicator={<Loader />}
        spinning={detailRefereeSearchLoading}
        className="loaderGifPosition"
      >
        {/* send sms with templates */}
        {modalState.modalName === "SendEmailByTemplateToSiteModal" && (
          <SendEmailByTemplateToSiteModal
            isVisible={modalState.isVisible}
            selectedEmailItems={modalState.selectedEmailItems || []}
            siteData={modalState.refereeData || []}
            resetModalFn={closeModal}
          />
        )}

        <Row>
          <Col span={6}>
            <Filters
              searchPerson={searchPerson}
              handleFilterChange={handleFilterChange}
              clearDetailFilter={clearDetailFilter}
              detailFilter={detailFilter}
            />
          </Col>

          <Col span={17} className="ml-2">
            <FilterChips detailFilter={detailFilter} removeFilter={removeFilter} />

            {tableData.length !== 0 ? (
              <RefereeList tableData={tableData} openModal={openModal} />
            ) : (
              <Empty />
            )}
          </Col>
        </Row>
      </Spin>
    </div>
  );
};
export default DetailedRefereeSearch;
